import React, { useRef } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Form, DatePicker, Input, Select, Switch, Button, Space } from "antd"

import { Stack } from "@components/Page"
import { Bold } from "@components/Text"
import { InvestmentShape } from "@components/Investment/shapes"

import BeneficiariesTable from "../BeneficiariesTable"
import getNextDate from "./helpers/getNextDate"

const LABEL_PROJECT = "Project"
const LABEL_START_DATE = "Start Date"
const LABEL_FREQUENCY = "Frequency"
const LABEL_NEXT_DATE = "Next Distribution"
const LABEL_ROUND_CENTS = "Round Cents"
const LABEL_SUBMIT = "Publish Schedule"

const FREQUENCY_OPTIONS = [
  // { value: "DAILY",         label: "Daily" },
  // { value: "WEEKLY",        label: "Weekly" },
  { value: "MONTHLY",       label: "Monthly" },
  { value: "QUARTERLY",     label: "Quarterly" },
  { value: "SEMI_ANNUALLY", label: "Semi-Annually" },
  { value: "ANNUALLY",      label: "Annually" },
]

const startDateItemProps = {
  name: "startDate",
  label: LABEL_START_DATE,
  rules: [{ required: true, message: `${LABEL_START_DATE} is required` }],
  normalize: (value) => value ? value.toISOString() : value,
  getValueProps: (value) => ({ value: value ? dayjs(value) : value }),
}

const frequencyItemProps = {
  name: "distributionFrequency",
  label: LABEL_FREQUENCY,
  rules: [{ required: true, message: `${LABEL_FREQUENCY} is required` }],
}

const datePickerProps = {
  picker: "date",
  format: "MM/DD/YYYY",
  placeholder: LABEL_START_DATE,
  allowClear: false,
}

const selectProps = {
  style: { width: 140 },
  options: FREQUENCY_OPTIONS,
  placeholder: LABEL_FREQUENCY,
}


const DistributionsScheduleForm = ({ projectId, projectName, investments }) => {
  const [ form ] = Form.useForm()
  const beneficiariesTableRef = useRef()

  const startDate = Form.useWatch("startDate", form)
  const frequency = Form.useWatch("distributionFrequency", form)
  const shouldRoundCents = Form.useWatch("shouldRoundCents", form)

  const nextDate = getNextDate(startDate, frequency)

  const onFinish = (payload) => {
    const totalAmount = beneficiariesTableRef.current.getTotalAmount()
    const selectedDistributions = beneficiariesTableRef.current.getSelectedDistributions()

    const distributions = selectedDistributions
      .map(({ investmentId, amount, investorAccountId, returnRate, tierId, investedAmount }) => ({
        investmentId,
        amount,
        tierId,
        returnRate,
        investorAccountId,
        investedAmount,
      }))

    console.log('payload', { ...payload, totalAmount, distributions })
  }

  const initialValues = {
    name: projectName,
    startDate: dayjs().toISOString(),
    shouldRoundCents: false,
    distributionFrequency: "MONTHLY",
    projectId,
  }

  const formProps = {
    layout: "inline",
    initialValues,
    form,
    onFinish,
  }

  return (
    <Stack>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Space size={16}>
          <span>
            {`${LABEL_PROJECT}:`} <Bold>{projectName}</Bold>
          </span>
          {
            nextDate && (
              <span>
                {`${LABEL_NEXT_DATE}:`} <Bold>{nextDate}</Bold>
              </span>
            )
          }
        </Space>

        <Form {...formProps}>
          <Form.Item name="projectId" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="name" hidden>
            <Input />
          </Form.Item>

          <Form.Item {...frequencyItemProps}>
            <Select {...selectProps} />
          </Form.Item>

          <Form.Item {...startDateItemProps}>
            <DatePicker {...datePickerProps} />
          </Form.Item>

          <Form.Item name="shouldRoundCents" label={LABEL_ROUND_CENTS} valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item style={{ margin: 0 }}>
            <Button type="primary" htmlType="submit">{LABEL_SUBMIT}</Button>
          </Form.Item>
        </Form>
      </div>

      <BeneficiariesTable
        ref={beneficiariesTableRef}
        investments={investments}
        distributionFrequency={frequency}
        shouldRoundCents={shouldRoundCents}
      />
    </Stack>
  )
}

DistributionsScheduleForm.propTypes = {
  projectId: PropTypes.string.isRequired,
  projectName: PropTypes.string.isRequired,
  investments: PropTypes.arrayOf(InvestmentShape).isRequired,
}

export default DistributionsScheduleForm
